// src/pages/DoorloopjesOverview.js

import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import { BlocksRenderer } from '@strapi/blocks-react-renderer';

function extractTextFromRichText(richText) {
  if (!richText || !Array.isArray(richText) || richText.length === 0) return 'Geen beschrijving beschikbaar.';
  return richText[0]?.children[0]?.text || '';
}

function DoorloopjesOverview() {
  const { doorloopjes: allDoorloopjes, currentUser, loading: contextLoading } = useDoorloopjes();

  const [activeFilter, setActiveFilter] = useState(null);
  const [selectedBook, setSelectedBook] = useState('all');
  const [activeThemeSlug, setActiveThemeSlug] = useState(null);

  // Mobiel detectie + collapsibles
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  const [introCollapsed, setIntroCollapsed] = useState(isMobile);
  const [themesCollapsed, setThemesCollapsed] = useState(isMobile);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => {
      setIsMobile(e.matches);
      setIntroCollapsed(e.matches);
      setThemesCollapsed(e.matches);
    };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  const availableBooks = useMemo(() => {
    if (!allDoorloopjes) return [];
    const bookNumbers = allDoorloopjes.map(d => parseInt(d.book, 10)).filter(num => !isNaN(num));
    return Array.from(new Set(bookNumbers)).sort((a, b) => a - b);
  }, [allDoorloopjes]);

  const filteredDoorloopjes = useMemo(() => {
    let list = allDoorloopjes;

    if (selectedBook !== 'all') {
      list = list.filter(d => d.book === parseInt(selectedBook, 10));
    }

    if (activeFilter && currentUser) {
      switch (activeFilter) {
        case 'opgeslagen': {
          const favoriteIds = new Set(currentUser.favorite_doorloopjes?.map(fav => fav.id));
          list = list.filter(d => favoriteIds.has(d.id));
          break;
        }
        case 'bezig': {
          const inProgressIds = new Set(currentUser.inprogress_doorloopjes?.map(d => d.id));
          list = list.filter(d => inProgressIds.has(d.id));
          break;
        }
        case 'afgerond': {
          const completedIds = new Set(currentUser.completed_doorloopjes?.map(d => d.id));
          list = list.filter(d => completedIds.has(d.id));
          break;
        }
        default:
          break;
      }
    }

    return list;
  }, [allDoorloopjes, selectedBook, activeFilter, currentUser]);

  const { groupedDoorloopjes, themes } = useMemo(() => {
    const grouped = filteredDoorloopjes.reduce((acc, doorloopje) => {
      const themeSlug = doorloopje.theme?.slug || 'Overig';
      if (!acc[themeSlug]) acc[themeSlug] = [];
      acc[themeSlug].push(doorloopje);
      return acc;
    }, {});

    // Custom theme order
    const themeOrder = [
      'gedrag-en-relaties',           // GR
      'curriculumontwerp',            // CU
      'uitleggen-en-voordoen',        // UV
      'bevragen-en-feedback',         // BF
      'oefenen-en-terughalen',        // OT
      'modus-b-lesgeven'              // Modus B
    ];

    // Sort themes by custom order, with unknown themes at the end alphabetically
    const sortedThemes = Object.keys(grouped).sort((a, b) => {
      const indexA = themeOrder.indexOf(a);
      const indexB = themeOrder.indexOf(b);

      // If both are in the custom order, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only A is in custom order, it comes first
      if (indexA !== -1) return -1;

      // If only B is in custom order, it comes first
      if (indexB !== -1) return 1;

      // If neither is in custom order, sort alphabetically
      return a.localeCompare(b);
    });

    return { groupedDoorloopjes: grouped, themes: sortedThemes };
  }, [filteredDoorloopjes]);

  const completedDoorloopjeIds = useMemo(() => {
    if (!currentUser?.completed_doorloopjes) {
      return new Set();
    }
    return new Set(currentUser.completed_doorloopjes.map(d => d.id));
  }, [currentUser]);

  // Actieve thema-link bij scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter(e => e.isIntersecting);
        if (visibleEntries.length > 0) {
          const lastVisibleEntry = visibleEntries[visibleEntries.length - 1];
          setActiveThemeSlug(lastVisibleEntry.target.id);
        }
      },
      { rootMargin: '0px 0px -70% 0px', threshold: 0 }
    );

    const themeElements = document.querySelectorAll('.theme-group');
    themeElements.forEach(el => observer.observe(el));

    return () => {
      themeElements.forEach(el => observer.unobserve(el));
    };
  }, [groupedDoorloopjes]);

  const handleScrollToTheme = (themeSlug) => {
    const element = document.getElementById(themeSlug);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setActiveThemeSlug(themeSlug);
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(prevFilter => (prevFilter === filter ? null : filter));
  };

  if (contextLoading) return <p>Doorloopjes laden...</p>;

  return (
    <div className="overview-page-grid">
      <main className="overview-main-content">
        {/* Titel */}
        <div className="overview-header mobile-tight">
          <h1>Doorloopjes</h1>
        </div>

        {isMobile ? (
          <div className="mobile-stack">
            {/* --- Twee knoppen naast elkaar --- */}
            <div className="collapsible-toggle-group">
              <button
                type="button"
                className="collapsible-toggle"
                aria-expanded={!introCollapsed}
                onClick={() => setIntroCollapsed(v => !v)}
              >
                {introCollapsed ? 'Toon uitleg' : 'Verberg uitleg'}
              </button>

              <button
                type="button"
                className="collapsible-toggle"
                aria-expanded={!themesCollapsed}
                onClick={() => setThemesCollapsed(v => !v)}
              >
                {themesCollapsed ? 'Toon thema’s' : 'Verberg thema’s'}
              </button>
            </div>

            {!introCollapsed && (
              <div className="collapsible mobile-gap">
                <p>
                  Een doorloopje helpt je stap voor stap je les te verbeteren. De doorloopjes staan per thema, zoals Bevragen en Feedback of Gedrag en Relaties. Filter op thema of op boek. Sla doorloopjes op en vink af wat je hebt gedaan. Weet je niet waar je begint? Vraag het Doortje, je assistent.
                </p>
              </div>
            )}

            {!themesCollapsed && (
              <div className="collapsible mobile-gap">
                <nav className="mobile-themes">
                  {themes.map(themeSlug => {
                    const themeTitle = groupedDoorloopjes[themeSlug][0]?.theme?.title || themeSlug.replace(/-/g, ' ');
                    return (
                      <a
                        key={themeSlug}
                        href={`#${themeSlug}`}
                        className={activeThemeSlug === themeSlug ? 'active' : ''}
                        onClick={(e) => {
                          e.preventDefault();
                          handleScrollToTheme(themeSlug);
                        }}
                      >
                        {themeTitle}
                      </a>
                    );
                  })}
                </nav>
              </div>
            )}

            {/* Filters */}
            <div className="filter-bar mobile-gap">
              <select className="filter-dropdown" value={selectedBook} onChange={(e) => setSelectedBook(e.target.value)}>
                <option value="all">Filter op boek</option>
                {availableBooks.map(bookNum => <option key={bookNum} value={bookNum}>Boek {bookNum}</option>)}
              </select>
              <button className={`filter-btn ${activeFilter === 'opgeslagen' ? 'active' : ''}`} onClick={() => handleFilterClick('opgeslagen')}>Opgeslagen</button>
              <button className={`filter-btn ${activeFilter === 'bezig' ? 'active' : ''}`} onClick={() => handleFilterClick('bezig')}>Bezig</button>
              <button className={`filter-btn ${activeFilter === 'afgerond' ? 'active' : ''}`} onClick={() => handleFilterClick('afgerond')}>Afgerond</button>
            </div>
          </div>
        ) : (
          <>
            <div className="collapsible">
              <p>
                Een doorloopje helpt je stap voor stap je les te verbeteren. De doorloopjes staan per thema, zoals Bevragen en Feedback of Gedrag en Relaties. Filter op thema of op boek. Sla doorloopjes op en vink af wat je hebt gedaan. Weet je niet waar je begint? Vraag het Doortje, je assistent.
              </p>
            </div>

            <div className="filter-bar">
              <select className="filter-dropdown" value={selectedBook} onChange={(e) => setSelectedBook(e.target.value)}>
                <option value="all">Filter op boek</option>
                {availableBooks.map(bookNum => <option key={bookNum} value={bookNum}>Boek {bookNum}</option>)}
              </select>
              <button className={`filter-btn ${activeFilter === 'opgeslagen' ? 'active' : ''}`} onClick={() => handleFilterClick('opgeslagen')}>Opgeslagen</button>
              <button className={`filter-btn ${activeFilter === 'bezig' ? 'active' : ''}`} onClick={() => handleFilterClick('bezig')}>Bezig</button>
              <button className={`filter-btn ${activeFilter === 'afgerond' ? 'active' : ''}`} onClick={() => handleFilterClick('afgerond')}>Afgerond</button>
            </div>
          </>
        )}

        {/* Themagroepen */}
        {themes.length > 0 ? themes.map(themeSlug => {
          const themeData = groupedDoorloopjes[themeSlug][0]?.theme;
          const doorloopjesInTheme = groupedDoorloopjes[themeSlug];
          const totalInTheme = doorloopjesInTheme.length;
          const completedInTheme = doorloopjesInTheme.filter(d => completedDoorloopjeIds.has(d.id)).length;
          const progressPercentage = totalInTheme > 0 ? (completedInTheme / totalInTheme) * 100 : 0;

          return (
            <div key={themeSlug} id={themeSlug} className="theme-group">
              <div className="theme-header">
                <h2>{themeData?.title || themeSlug.replace(/-/g, ' ')}</h2>
                <div className="theme-progress-container">
                  <div className="theme-progress-bar">
                    <div className="theme-progress-bar-inner" style={{ width: `${progressPercentage}%` }}></div>
                  </div>
                  <span className="theme-progress-label">
                    {completedInTheme} van de {totalInTheme} afgerond
                  </span>
                </div>
              </div>
              {themeData?.description ? (
                <div className="theme-description"><BlocksRenderer content={themeData.description} /></div>
              ) : (
                <p>Geen beschrijving voor dit thema.</p>
              )}
              <div className="doorloopje-list">
                {groupedDoorloopjes[themeSlug].map(doorloopje => {
                  const descriptionSnippet = `${extractTextFromRichText(doorloopje.description).substring(0, 100)}...`;
                  const iconUrl = doorloopje.thumbnail?.formats?.thumbnail?.url || doorloopje.thumbnail?.url;
                  return (
                    <Link to={`/doorloopjes/${doorloopje.slug}`} key={doorloopje.id} className="doorloopje-card">
                      {iconUrl ? <img src={iconUrl} alt={doorloopje.title} className="card-icon" loading="lazy" /> : <div className="card-icon"></div>}
                      <div className="card-text"><h3>{doorloopje.title}</h3><p>{descriptionSnippet}</p></div>
                      <div className="card-arrow">></div>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        }) : <p>Geen doorloopjes gevonden die aan de filters voldoen.</p>}
      </main>

      {/* Desktop sidebar */}
      {!isMobile && (
        <aside className="overview-sidebar">
          <h3>Thema's</h3>
          <nav>
            {themes.map(themeSlug => {
              const themeTitle = groupedDoorloopjes[themeSlug][0]?.theme?.title || themeSlug.replace(/-/g, ' ');
              return (
                <a
                  key={themeSlug}
                  href={`#${themeSlug}`}
                  className={activeThemeSlug === themeSlug ? 'active' : ''}
                  onClick={(e) => {
                    e.preventDefault();
                    handleScrollToTheme(themeSlug);
                  }}
                >
                  {themeTitle}
                </a>
              );
            })}
          </nav>
        </aside>
      )}
    </div>
  );
}

export default DoorloopjesOverview;
